import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";

import { ApiError, api } from "../../api/client.ts";
import { favoriteKey, type FavoriteEntityType } from "../../api/favorites.ts";
import {
  useInvalidateTrades,
  useLookups,
  useQuickValues,
  type CatalogItem,
  type QuickValueField,
} from "../../api/hooks.ts";
import {
  formatLot,
  formatPips,
  formatR,
  formatSignedPips,
  parseDecimal,
  toneOf,
} from "../../domain/format.ts";
import {
  closePreview,
  disagreement,
  planPreview,
  typedClosePreview,
  typedPlanPreview,
  type Direction,
} from "../../domain/pips.ts";
import { usePasteImages } from "../../ui/clipboard.ts";
import { Icon } from "../../ui/Icon.tsx";
import { Modal } from "../../ui/Modal.tsx";
import { useFinePointer } from "../../ui/pointer.ts";
import { SaveButton } from "../../ui/SaveButton.tsx";
import { Suggestions } from "../../ui/Suggestions.tsx";
import { StatusBlock } from "../../ui/StatusBlock.tsx";
import { holdShots } from "./pendingShots.ts";
import { DRAFT_KEY } from "./repeatTrade.ts";
import { MAX_IMAGES } from "./TradeImages.tsx";

/**
 * "Nuevo trade": UN solo formulario, como en la plantilla. Se abre encima de la
 * bitacora y se publica de una vez.
 *
 * Por dentro son tres llamadas —crear, cerrar y revisar— porque el dominio
 * separa el plan de la ejecucion y de la revision (SPEC 8). El usuario no tiene
 * por que enterarse: el boton es uno.
 *
 * LOS PIPS SON LA ENTRADA (ADR 0007): simbolo, direccion, pips de riesgo, pips
 * de objetivo y la captura es el registro minimo. Los precios van despues,
 * plegados y opcionales; si estan, son contexto, y si no cuadran con los pips
 * se dice antes de publicar.
 *
 * Lo unico que NO se escribe a mano es el R. Lo calcula la app mientras
 * escribes los pips y lo confirma el servidor al guardar (SPEC 15.1): dejar
 * teclear el R seria dejar teclear la nota del examen.
 */

/* La clave vive en `repeatTrade.ts`, que es quien mas la necesita: el boton
   Repetir escribe el borrador que esta pantalla lee, y dos copias de la cadena
   serian dos sitios donde cambiarla. */

const TIMEFRAMES = [
  { value: "m1", label: "1 minuto" },
  { value: "m5", label: "5 minutos" },
  { value: "m15", label: "15 minutos" },
  { value: "m30", label: "30 minutos" },
  { value: "h1", label: "1 hora" },
  { value: "h4", label: "4 horas" },
  { value: "d1", label: "Diario" },
  { value: "w1", label: "Semanal" },
  { value: "mn1", label: "Mensual" },
];

/**
 * Las cinco temporalidades que el propietario usa de verdad, a un toque encima
 * del desplegable. Son fijas EN EL CODIGO y no una fila de catalogo: una
 * temporalidad es un valor del enum del contrato, asi que no hay nada que
 * guardar ni que administrar. El desplegable se queda con las nueve.
 */
const FAV_TIMEFRAMES = ["m1", "m5", "m15", "m30", "h1"] as const;

const MIND = [
  { value: 1, face: "\u{1F616}", help: "1 de 5: estres, prisa o cansancio" },
  { value: 2, face: "\u{1F615}", help: "2 de 5: dudas o presion" },
  { value: 3, face: "\u{1F610}", help: "3 de 5: neutral" },
  { value: 4, face: "\u{1F642}", help: "4 de 5: enfocado y tranquilo" },
  { value: 5, face: "\u{1F60E}", help: "5 de 5: claridad total" },
];

type Draft = {
  title: string;
  instrumentId: string;
  brokerId: string;
  strategyId: string;
  setupId: string;
  accountId: string;
  direction: Direction;
  riskPips: string;
  rewardPips: string;
  resultPips: string;
  lotSize: string;
  entryPrice: string;
  stopPrice: string;
  targetPrice: string;
  exitPrice: string;
  openedAt: string;
  holdMinutes: string;
  timeframe: string;
  exitReason: string;
  maePips: string;
  mfePips: string;
  emotionIds: string[];
  mind: number;
  followedPlan: boolean;
  brokenRuleIds: string[];
  notes: string;
  lesson: string;
};

const EMPTY: Draft = {
  title: "",
  instrumentId: "",
  brokerId: "",
  strategyId: "",
  setupId: "",
  accountId: "",
  direction: "buy",
  riskPips: "",
  rewardPips: "",
  resultPips: "",
  lotSize: "",
  entryPrice: "",
  stopPrice: "",
  targetPrice: "",
  exitPrice: "",
  openedAt: "",
  holdMinutes: "",
  timeframe: "",
  exitReason: "",
  maePips: "",
  mfePips: "",
  emotionIds: [],
  mind: 3,
  followedPlan: true,
  brokenRuleIds: [],
  notes: "",
  lesson: "",
};

/** El borrador se guarda en cada cambio (SPEC 15.5). Nunca credenciales. */
function loadDraft(): Draft {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);

    return raw === null ? EMPTY : { ...EMPTY, ...(JSON.parse(raw) as Partial<Draft>) };
  } catch {
    return EMPTY;
  }
}

export function TradeModal(props: { onClose: () => void }): ReactNode {
  const lookups = useLookups();
  const invalidate = useInvalidateTrades();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<Draft>(() => ({ ...loadDraft(), openedAt: loadDraft().openedAt || localNow() }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /* El pliegue de los precios empieza abierto solo si el borrador recuperado
     ya traia alguno: lo que se escribio tiene que verse. */
  const [pricesOpen, setPricesOpen] = useState(
    () => [draft.entryPrice, draft.stopPrice, draft.targetPrice, draft.exitPrice].some((v) => v !== ""),
  );

  /* Las capturas pegadas con Ctrl+V. Aqui NO se pueden subir —el contrato sube
     a `POST /trades/{id}/images` y ese id no existe hasta guardar—, asi que se
     recogen y se entregan al detalle, que es el que tiene cola, progreso por
     archivo y reintento (ver `pendingShots`). Los `File` NO van al borrador de
     `localStorage`: ni caben ni se pueden serializar. */
  const [shots, setShots] = useState<readonly File[]>([]);
  const fine = useFinePointer();

  /* El tope de SPEC 14 es el de la zona de subida: pegar la sexta no serviria
     de nada, porque alli no cabria. */
  usePasteImages(shots.length < MAX_IMAGES, (files) =>
    setShots((previous) => [...previous, ...files].slice(0, MAX_IMAGES)),
  );

  const shotUrls = useMemo(() => shots.map((file) => URL.createObjectURL(file)), [shots]);

  /* Cada `createObjectURL` retiene el archivo hasta que se revoca. La limpieza
     corre tambien al cambiar la lista, asi que quitar una captura libera la
     suya y no solo la del cierre del modal. */
  useEffect(
    () => () => {
      for (const url of shotUrls) URL.revokeObjectURL(url);
    },
    [shotUrls],
  );

  useEffect(() => {
    /* Esto corre en CADA pulsacion de tecla. En modo privado o con las cookies
       de sitio bloqueadas, `setItem` lanza; sin la guarda, la excepcion sube por
       el efecto y tumba la pantalla a media operacion. Perder el borrador es
       malo; perder el formulario entero, peor. */
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // Sin sitio donde guardar: el borrador dura lo que dure la pestana.
    }
  }, [draft]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void =>
    setDraft((previous) => ({ ...previous, [key]: value }));

  const instruments = lookups.data?.instruments.filter((item) => item.isActive) ?? [];
  const instrument = instruments.find((item) => item.id === draft.instrumentId);
  const catalogs = lookups.data?.catalogs;
  const rules = lookups.data?.planRules.filter((rule) => rule.isActive) ?? [];

  /*
   * LOS CHIPS SON SUS FAVORITOS DE VERDAD, no "los cuatro primeros".
   *
   * Eran los cuatro primeros del catalogo, que es una lista ordenada por
   * `sortOrder` y que no tiene nada que ver con lo que este usuario opera: el
   * atajo apuntaba a cuatro simbolos que igual no ha tocado nunca. Ahora salen
   * los que ha marcado con estrella en Indices y pares y en Brokers y listas.
   *
   * Vienen de `lookups.favorites`, que trae solo los OFRECIBLES —los que apuntan
   * a algo activo—, y no de `GET /favorites`, que trae tambien los archivados:
   * un chip que ofrece un setup retirado ofrece elegir algo que el servidor ya no
   * acepta.
   *
   * Sin ninguna estrella la fila no se pinta: `Favs` devuelve null con la lista
   * vacia. Una fila de chips vacia deja un hueco que parece un fallo de carga.
   */
  const marks = new Set(
    (lookups.data?.favorites ?? []).map((item) => favoriteKey(item.entityType, item.entityId)),
  );

  const favoritesOf = <T extends { id: string }>(
    type: FavoriteEntityType,
    pool: readonly T[],
  ): T[] => pool.filter((item) => marks.has(favoriteKey(type, item.id)));

  /* Se filtra el CATALOGO y no la lista de marcas, para que los chips salgan en
     el mismo orden que el desplegable que tienen justo debajo: dos ordenes
     distintos para las mismas opciones obligan a buscar dos veces. */
  const favoriteInstruments = favoritesOf("instrument", instruments);
  const favoriteBrokers = favoritesOf("broker", list(catalogs, "brokers"));
  const favoriteSetups = favoritesOf("setup", list(catalogs, "setups"));
  const favoriteStrategies = favoritesOf("strategy", list(catalogs, "estrategias"));
  const favoriteConfluences = favoritesOf("confluence", list(catalogs, "confluencias"));
  const noFavorites =
    favoriteInstruments.length
      + favoriteBrokers.length
      + favoriteSetups.length
      + favoriteStrategies.length
      + favoriteConfluences.length === 0;

  /* EL BROKER FAVORITO VIENE ELEGIDO, pero solo si hay UNO.
     Lo pidio el propietario al ver "Sin broker" con Weltrade ya marcado con
     estrella. Con dos o mas favoritos no se elige ninguno: adivinar cual de dos
     es peor que no adivinar, porque un valor que puso el sistema no se revisa
     igual que un hueco vacio, y el broker decide de que cuenta cuelga la
     operacion.

     Solo toca un borrador VACIO en ese campo: si el usuario ya eligio -- o si
     volvio a un borrador guardado, o si pulso Repetir -- no se le cambia nada.
     Y se hace en un efecto y no en el valor inicial porque los favoritos llegan
     de `lookups`, que es una peticion: al montar todavia no estan. */
  /* El identificador y no el array: `favoritesOf` devuelve uno nuevo en cada
     render, asi que con el array en las dependencias el efecto correria siempre.
     Aqui no romperia nada -- `setDraft` devolviendo `previous` no repinta -- pero
     es el mismo descuido que hoy mismo hubo que arreglar en `Modal`, y un efecto
     que corre en cada render es una trampa esperando a que alguien le anada algo
     dentro. */
  const onlyFavoriteBroker = favoriteBrokers.length === 1 ? favoriteBrokers[0]?.id ?? null : null;

  useEffect(() => {
    if (onlyFavoriteBroker === null) return;

    setDraft((previous) =>
      previous.brokerId === "" ? { ...previous, brokerId: onlyFavoriteBroker } : previous,
    );
  }, [onlyFavoriteBroker]);

  /* LOS NUMEROS A UN TOQUE (ADR 0011). El id del chip es el propio numero YA
     FORMATEADO, porque el borrador guarda los campos como cadena: asi `value`
     compara lo mismo que se escribiria a mano y el chip sale marcado cuando el
     numero coincide, lo haya puesto el chip o el teclado. */
  const quickValues = useQuickValues();

  const quickOf = (field: QuickValueField): { id: string; name: string }[] =>
    (quickValues.data ?? [])
      .filter((item) => item.field === field)
      .map((item) => {
        const text = field === "lotSize" ? formatLot(item.value) : formatPips(item.value);

        return { id: text, name: text };
      });

  const entry = parseDecimal(draft.entryPrice);
  const stop = parseDecimal(draft.stopPrice);
  const target = parseDecimal(draft.targetPrice);
  const exit = parseDecimal(draft.exitPrice);
  const mfe = parseDecimal(draft.mfePips);

  /* LOS PIPS SON LA ENTRADA (ADR 0007): el riesgo es lo que arriesgo (el
     stop) y el objetivo lo que busco, tal cual los ensena la plataforma. */
  const riskTyped = parseDecimal(draft.riskPips);
  const rewardTyped = parseDecimal(draft.rewardPips);
  const resultTyped = parseDecimal(draft.resultPips);

  const typedPlan =
    riskTyped !== null && rewardTyped !== null && riskTyped >= 0 && rewardTyped > 0
      ? typedPlanPreview(riskTyped, rewardTyped)
      : null;

  /* Los precios son CONTEXTO. Los tres del plan van juntos o ninguno, que es
     lo que exige el contrato; de ellos se derivan pips SOLO para contrastarlos
     con los escritos. Sin pips escritos, el plan sale de aqui, que el servidor
     sigue aceptando. */
  const pricesGiven = [entry, stop, target].filter((price) => price !== null).length;
  const pricesComplete = pricesGiven === 3;
  const pricesPartial = pricesGiven > 0 && !pricesComplete;

  const pricePlan =
    instrument !== undefined && entry !== null && stop !== null && target !== null
      ? planPreview(entry, stop, target, draft.direction, instrument.pipSize)
      : null;

  const plan = typedPlan ?? pricePlan;

  const priceClose =
    instrument !== undefined && plan !== null && entry !== null && exit !== null
      ? closePreview(entry, exit, draft.direction, instrument.pipSize, plan.plannedRiskPips, mfe)
      : null;

  /* Con pips escritos mandan los pips; el precio de salida solo cierra por si
     mismo cuando no hay pips escritos. */
  const close =
    plan !== null && resultTyped !== null
      ? typedClosePreview(resultTyped, plan.plannedRiskPips, mfe)
      : priceClose;

  /* Si estan las dos cosas y no cuadran, se dice: no se corrige ni se descarta
     ninguna (ADR 0007). El servidor lo comprueba igual y lo deja senalado. */
  const verdict = disagreement(
    {
      plannedRiskPips: typedPlan?.plannedRiskPips ?? null,
      plannedRewardPips: typedPlan?.plannedRewardPips ?? null,
      resultPips: resultTyped,
    },
    {
      plannedRiskPips: pricePlan?.plannedRiskPips ?? null,
      plannedRewardPips: pricePlan?.plannedRewardPips ?? null,
      resultPips: priceClose?.resultPips ?? null,
    },
  );

  /* El stop del lado equivocado solo se puede juzgar con precios: sin ellos,
     el riesgo es una distancia y no tiene lado. */
  const wrongSide =
    pricePlan !== null && (pricePlan.stopOnWrongSide || pricePlan.takeProfitOnWrongSide);

  /* Cerrar solo con el precio de salida exige el de entrada: sin el no hay de
     donde derivar el resultado, y el servidor respondera 400. */
  const exitNeedsEntry = resultTyped === null && exit !== null && entry === null;

  const planOk = plan !== null && !wrongSide && !pricesPartial;
  const canSave = draft.instrumentId !== "" && planOk && !exitNeedsEntry && !saving;

  async function publish(): Promise<void> {
    if (instrument === undefined || plan === null) return;

    setSaving(true);
    setError(null);

    try {
      const openedAtUtc = new Date(draft.openedAt).toISOString();

      const created = await api.post<{ id: string }>("/api/v1/trades", {
        instrumentId: draft.instrumentId,
        brokerId: blank(draft.brokerId),
        accountId: blank(draft.accountId),
        strategyId: blank(draft.strategyId),
        setupId: blank(draft.setupId),
        direction: draft.direction,
        plannedAtUtc: new Date(new Date(draft.openedAt).getTime() - 3_600_000).toISOString(),
        plannedRiskPips: typedPlan?.plannedRiskPips ?? null,
        plannedRewardPips: typedPlan?.plannedRewardPips ?? null,
        plannedEntryPrice: pricesComplete ? entry : null,
        stopLossPrice: pricesComplete ? stop : null,
        takeProfitPrice: pricesComplete ? target : null,
        entryTimeframe: blank(draft.timeframe),
        confidenceBefore: draft.mind,
        thesis: blank(draft.title),
        actualEntryPrice: entry,
        openedAtUtc,
        lotSize: parseDecimal(draft.lotSize),
      });

      /* Con pips ganados o perdidos —o con precio de salida, si hay de entrada—
         la operacion nace y se cierra de una vez. Si no, queda abierta y se
         cierra despues desde su detalle. */
      let version = 0;

      if (close !== null) {
        const minutes = Number(draft.holdMinutes);
        const closedAtUtc = new Date(
          new Date(draft.openedAt).getTime() + (Number.isFinite(minutes) && minutes > 0 ? minutes : 30) * 60_000,
        ).toISOString();

        const fresh = await api.get<{ version: number }>(`/api/v1/trades/${created.id}`);

        await api.post<void>(`/api/v1/trades/${created.id}/close`, {
          resultPips: resultTyped,
          actualExitPrice: exit,
          closedAtUtc,
          exitReason: blank(draft.exitReason) ?? "manual",
          maePips: parseDecimal(draft.maePips),
          mfePips: mfe,
          executionGrade: null,
          version: fresh.version,
        });
      }

      const needsReview =
        draft.emotionIds.length > 0 ||
        draft.notes !== "" ||
        draft.lesson !== "" ||
        !draft.followedPlan ||
        rules.length > 0;

      if (needsReview) {
        const fresh = await api.get<{ version: number }>(`/api/v1/trades/${created.id}`);

        version = fresh.version;

        await api.put<void>(`/api/v1/trades/${created.id}/review`, {
          emotions: draft.emotionIds.map((id) => ({
            emotionId: id,
            moment: "during",
            intensity: 3,
            note: null,
          })),
          onlyMoment: null,
          ruleChecks: rules.map((rule) => ({
            planRuleId: rule.id,
            isFollowed: draft.followedPlan || !draft.brokenRuleIds.includes(rule.id),
            note: null,
          })),
          mistakeIds: null,
          confluenceIds: null,
          whatWentWell: blank(draft.notes),
          whatToImprove: null,
          lesson: blank(draft.lesson),
          version,
        });
      }

      /* Misma guarda que al guardar: aqui la operacion YA se publico, y dejar
         que `removeItem` lance mandaria el exito al `catch` de abajo, que lo
         mostraria como un fallo y no navegaria al detalle. */
      try {
        window.localStorage.removeItem(DRAFT_KEY);
      } catch {
        // Sin almacenamiento no hay borrador viejo que limpiar.
      }

      /* Lo pegado viaja CON SU operacion: la zona de subida del detalle lo
         recoge al montarse, si es el de esta operacion, y lo sube de una en una.
         Las vistas previas no van: las revoca el efecto de arriba al desmontar. */
      if (shots.length > 0) holdShots(created.id, shots.map((file) => ({ file })));

      invalidate();
      navigate(`/trades/${created.id}`);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure : null);
    } finally {
      setSaving(false);
    }
  }

  /* Mientras haya algo escrito, ni Escape cierra. Lo que se ha tecleado aqui
     cuesta minutos de recordar, y el gesto de cerrar cuesta uno. Una captura
     pegada cuenta igual: el portapapeles ya no la tiene. */
  const dirty =
    JSON.stringify({ ...draft, openedAt: "" }) !== JSON.stringify({ ...EMPTY, openedAt: "" }) ||
    shots.length > 0;

  return (
    <Modal
      title="Nuevo trade"
      dirty={dirty}
      onClose={props.onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={props.onClose}>
            Cancelar
          </button>
          {/* Sin "Guardado": al publicar se navega al detalle de la operacion
              recien creada, y aterrizar en ella es la confirmacion. Lo que si
              hacia falta aqui es el "Procesando" con el boton desactivado. */}
          <SaveButton
            label="Publicar trade"
            busyLabel="Procesando"
            busy={saving}
            saved={false}
            disabled={!canSave}
            onClick={() => void publish()}
          />
        </>
      }
    >
      <StatusBlock
            loading={lookups.isPending}
            error={lookups.error}
            onRetry={() => void lookups.refetch()}
          >
            {error !== null && (
              <p className="errbox">{error.message}</p>
            )}

            <div className="grid2">
              <div className="f" style={{ gridColumn: "1/-1" }}>
                <span>Titulo del trade</span>
                <input
                  className="ctl"
                  value={draft.title}
                  placeholder="Ej. Ruptura del rango asiatico con volumen"
                  onChange={(event) => set("title", event.target.value)}
                />
                <Suggestions
                  field="title"
                  value={draft.title}
                  onPick={(frase) => set("title", frase)}
                />
                <small className="hint">
                  Toca una sugerencia para usarla, o escribe la tuya en Ajustes. Se guarda como la
                  tesis: el por que, escrito antes de entrar.
                </small>
              </div>

              <div className="f">
                <span>Indice o par</span>
                <Favs
                  label="Simbolos favoritos"
                  items={favoriteInstruments.map((item) => ({ id: item.id, name: item.code }))}
                  value={draft.instrumentId}
                  onPick={(id) => set("instrumentId", id)}
                />
                {/* El <span> de titulo no etiqueta nada: estos tres campos
                    llevan chips de favoritos dentro y no caben en un <label>
                    sin descolocar la plantilla. El nombre accesible lo pone el
                    aria-label. */}
                <select
                  className="ctl"
                  aria-label="Indice o par"
                  value={draft.instrumentId}
                  onChange={(event) => set("instrumentId", event.target.value)}
                >
                  <option value="">Elige el simbolo</option>
                  {ordered(instruments, favoriteInstruments).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.code} · {item.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="f">
                <span>Broker</span>
                <Favs
                  label="Brokers favoritos"
                  items={favoriteBrokers}
                  value={draft.brokerId}
                  onPick={(id) => set("brokerId", id)}
                />
                <select
                  className="ctl"
                  aria-label="Broker"
                  value={draft.brokerId}
                  onChange={(event) => set("brokerId", event.target.value)}
                >
                  <option value="">Sin broker</option>
                  {ordered(list(catalogs, "brokers"), favoriteBrokers).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="f">
                <span>Direccion</span>
                <div className="seg" role="group" aria-label="Direccion">
                  <button
                    type="button"
                    data-v="long"
                    className={draft.direction === "buy" ? "active" : ""}
                    aria-pressed={draft.direction === "buy"}
                    onClick={() => set("direction", "buy")}
                  >
                    Long
                  </button>
                  <button
                    type="button"
                    data-v="short"
                    className={draft.direction === "sell" ? "active" : ""}
                    aria-pressed={draft.direction === "sell"}
                    onClick={() => set("direction", "sell")}
                  >
                    Short
                  </button>
                </div>
              </div>

              <div className="f">
                <span>
                  Resultado <i className="opt">lo calcula la app</i>
                </span>
                {/* De solo lectura DE VERDAD: el `pointer-events: none` del CSS
                    solo aparta el raton, y estos seguian siendo enfocables con
                    el tabulador y activables con Enter. `disabled` los saca del
                    recorrido y el lector de pantalla los anuncia como lo que
                    son: el resultado que calcula la app, no una eleccion. */}
                <div className="seg auto" aria-live="polite">
                  <button
                    type="button"
                    disabled
                    data-v="win"
                    className={close?.outcome === "win" ? "active" : ""}
                  >
                    Ganado
                  </button>
                  <button
                    type="button"
                    disabled
                    data-v="loss"
                    className={close?.outcome === "loss" ? "active" : ""}
                  >
                    Perdido
                  </button>
                  <button
                    type="button"
                    disabled
                    data-v="be"
                    className={close?.outcome === "breakEven" ? "active" : ""}
                  >
                    Break even
                  </button>
                </div>
              </div>

              {/* Un <div> y no un <label>: mete chips dentro, y un grupo de
                  botones dentro de un <label> hace que pulsar un chip reenvie el
                  foco al input. Es el mismo motivo que ya esta escrito para los
                  tres desplegables de arriba; el nombre accesible lo pone el
                  aria-label del input. */}
              <div className="f">
                <span>Pips de riesgo</span>
                <Favs
                  label="Pips de riesgo favoritos"
                  items={quickOf("riskPips")}
                  value={draft.riskPips}
                  onPick={(value) => set("riskPips", value)}
                />
                <input
                  className="ctl num"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-label="Pips de riesgo"
                  value={draft.riskPips}
                  placeholder="100"
                  onChange={(event) => set("riskPips", event.target.value)}
                />
                <span className="hint">Lo que arriesgas: la distancia al stop, como la ensena tu plataforma.</span>
                {error?.fieldError("plannedRiskPips") !== undefined && (
                  <span className="err">{error.fieldError("plannedRiskPips")}</span>
                )}
              </div>

              <div className="f">
                <span>Pips de objetivo</span>
                <Favs
                  label="Pips de objetivo favoritos"
                  items={quickOf("rewardPips")}
                  value={draft.rewardPips}
                  onPick={(value) => set("rewardPips", value)}
                />
                <input
                  className="ctl num"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-label="Pips de objetivo"
                  value={draft.rewardPips}
                  placeholder="300"
                  onChange={(event) => set("rewardPips", event.target.value)}
                />
                <span className="hint">Lo que buscas: la distancia al objetivo.</span>
                {error?.fieldError("plannedRewardPips") !== undefined && (
                  <span className="err">{error.fieldError("plannedRewardPips")}</span>
                )}
              </div>

              <label className="f">
                <span>
                  Pips ganados o perdidos <i className="opt">si ya cerraste</i>
                </span>
                <input
                  className="ctl num"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={draft.resultPips}
                  placeholder="+180"
                  onChange={(event) => set("resultPips", event.target.value)}
                />
                <span className="hint">
                  Con signo: +180 si ganaste, -100 si perdiste. En blanco queda abierta y se cierra
                  desde su detalle.
                </span>
                {error?.fieldError("resultPips") !== undefined && (
                  <span className="err">{error.fieldError("resultPips")}</span>
                )}
              </label>

              <div className="f">
                <span>
                  R multiplo <i className="opt">lo calcula la app</i>
                </span>
                <input
                  className={`ctl num ${close === null ? "" : toneOf(close.rMultiple)}`}
                  readOnly
                  tabIndex={-1}
                  value={close === null ? "—" : formatR(close.rMultiple)}
                />
              </div>

              <div className="f">
                <span>
                  Lotaje <i className="opt">opcional</i>
                </span>
                <Favs
                  label="Lotajes favoritos"
                  items={quickOf("lotSize")}
                  value={draft.lotSize}
                  onPick={(value) => set("lotSize", value)}
                />
                <input
                  className="ctl num"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-label="Lotaje"
                  value={draft.lotSize}
                  placeholder="0.10"
                  onChange={(event) => set("lotSize", event.target.value)}
                />
                <span className="hint">
                  Cuanto volumen metiste. No entra en el R ni en los pips: esta para que veas tu
                  patron de tamano y notes cuando te sales de tus casillas.
                </span>
                {error?.fieldError("lotSize") !== undefined && (
                  <span className="err">{error.fieldError("lotSize")}</span>
                )}
              </div>

              <div className="f" style={{ gridColumn: "1/-1" }}>
                <span>Setup</span>
                <Favs
                  label="Setups favoritos"
                  items={favoriteSetups}
                  value={draft.setupId}
                  onPick={(id) => set("setupId", id)}
                />
                <select
                  className="ctl"
                  aria-label="Setup"
                  value={draft.setupId}
                  onChange={(event) => set("setupId", event.target.value)}
                >
                  <option value="">Sin setup</option>
                  {list(catalogs, "setups").map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sin ninguna estrella no se pinta una fila de chips vacia; lo
                  que se dice es DONDE se ponen. El propietario lo dijo con estas
                  palabras: "no encontre donde puedo ponerlos como favoritos". */}
              {noFavorites && (
                <p className="hint" style={{ gridColumn: "1/-1", marginTop: -6 }}>
                  Marca con la estrella tus simbolos en <b>Indices y pares</b> y tus brokers y
                  setups en <b>Brokers y listas</b>, y apareceran aqui a un toque.
                </p>
              )}

              {/* Un <div> y no un <label>: dentro de una etiqueta, un clic en
                  uno de los chips se reenviaria al desplegable. Es el mismo
                  patron que el campo de Setup, que ya lleva favoritos. */}
              <div className="f">
                <span>Temporalidad</span>
                <div className="favs" role="group" aria-label="Temporalidades favoritas">
                  {FAV_TIMEFRAMES.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={draft.timeframe === value ? "favchip sel" : "favchip"}
                      aria-pressed={draft.timeframe === value}
                      title={timeframeLabel(value)}
                      onClick={() => set("timeframe", value)}
                    >
                      <Icon name="star" />
                      {value.toUpperCase()}
                    </button>
                  ))}
                </div>
                <select
                  className="ctl"
                  aria-label="Temporalidad"
                  value={draft.timeframe}
                  onChange={(event) => set("timeframe", event.target.value)}
                >
                  <option value="">Sin temporalidad</option>
                  {TIMEFRAMES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <label className="f">
                <span>Duracion (minutos)</span>
                <input
                  className="ctl num"
                  type="text"
                  inputMode="numeric"
                  value={draft.holdMinutes}
                  placeholder="30"
                  onChange={(event) => set("holdMinutes", event.target.value)}
                />
              </label>

              <label className="f">
                <span>Fecha y hora de entrada</span>
                <input
                  className="ctl num"
                  type="datetime-local"
                  value={draft.openedAt}
                  onChange={(event) => set("openedAt", event.target.value)}
                />
              </label>

              <div className="f">
                <span>
                  Estrategia <i className="opt">opcional</i>
                </span>
                <Favs
                  label="Estrategias favoritas"
                  items={favoriteStrategies}
                  value={draft.strategyId}
                  onPick={(id) => set("strategyId", id)}
                />
                <select
                  className="ctl"
                  aria-label="Estrategia"
                  value={draft.strategyId}
                  onChange={(event) => set("strategyId", event.target.value)}
                >
                  <option value="">Sin estrategia</option>
                  {ordered(list(catalogs, "estrategias"), favoriteStrategies).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* MAE y MFE son dos siglas y nadie nace sabiendolas: debajo de
                  cada una va lo que significa, en una linea. */}
              <label className="f">
                <span>
                  MAE en pips <i className="opt">opcional</i>
                </span>
                <input
                  className="ctl num"
                  type="text"
                  inputMode="decimal"
                  value={draft.maePips}
                  placeholder="14"
                  onChange={(event) => set("maePips", event.target.value)}
                />
                <span className="hint">
                  Lo mas lejos que el precio fue EN CONTRA mientras la operacion estaba viva. Es
                  una distancia: se escribe sin signo.
                </span>
              </label>

              <label className="f">
                <span>
                  MFE en pips <i className="opt">opcional</i>
                </span>
                <input
                  className="ctl num"
                  type="text"
                  inputMode="decimal"
                  value={draft.mfePips}
                  placeholder="130"
                  onChange={(event) => set("mfePips", event.target.value)}
                />
                <span className="hint">
                  Lo mas lejos que el precio fue A FAVOR, tambien sin signo. No puede ser menor que
                  los pips que ganaste: el precio llego al menos hasta donde saliste.
                </span>
              </label>

              {/* LOS PRECIOS, DESPUES Y PLEGADOS (ADR 0007). Registrar rapido y
                  completar en la revision, que es lo que pidio el propietario. */}
              <details
                className="fold"
                style={{ gridColumn: "1/-1", marginBottom: 0 }}
                open={pricesOpen}
                onToggle={(event) => setPricesOpen(event.currentTarget.open)}
              >
                <summary>
                  Precios, si los tienes
                  <i className="opt">opcionales</i>
                </summary>
                <div className="fold-b">
                  <p className="hint" style={{ marginBottom: 10 }}>
                    Los pips de arriba mandan. Los precios se guardan como contexto y, si no cuadran
                    con los pips, la app lo dice antes de publicar. Los tres del plan van juntos.
                  </p>
                  <div className="grid2">
                    <label className="f">
                      <span>Precio de entrada</span>
                      <input
                        className="ctl num"
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        value={draft.entryPrice}
                        placeholder="1000.00"
                        onChange={(event) => set("entryPrice", event.target.value)}
                      />
                      {error?.fieldError("plannedEntryPrice") !== undefined && (
                        <span className="err">{error.fieldError("plannedEntryPrice")}</span>
                      )}
                    </label>

                    <label className="f">
                      <span>Stop</span>
                      <input
                        className="ctl num"
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        value={draft.stopPrice}
                        placeholder="999.00"
                        onChange={(event) => set("stopPrice", event.target.value)}
                      />
                      {error?.fieldError("stopLossPrice") !== undefined && (
                        <span className="err">{error.fieldError("stopLossPrice")}</span>
                      )}
                    </label>

                    <label className="f">
                      <span>Objetivo</span>
                      <input
                        className="ctl num"
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        value={draft.targetPrice}
                        placeholder="1003.00"
                        onChange={(event) => set("targetPrice", event.target.value)}
                      />
                      {error?.fieldError("takeProfitPrice") !== undefined && (
                        <span className="err">{error.fieldError("takeProfitPrice")}</span>
                      )}
                    </label>

                    <label className="f">
                      <span>
                        Precio de salida <i className="opt">si ya cerraste</i>
                      </span>
                      <input
                        className="ctl num"
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        value={draft.exitPrice}
                        placeholder="1002.50"
                        onChange={(event) => set("exitPrice", event.target.value)}
                      />
                      {error?.fieldError("actualExitPrice") !== undefined && (
                        <span className="err">{error.fieldError("actualExitPrice")}</span>
                      )}
                    </label>
                  </div>

                  {pricesPartial && (
                    <p className="warnbox">
                      Los tres precios del plan van juntos: entrada, stop y objetivo. Escribe los que
                      faltan o deja los tres en blanco.
                    </p>
                  )}
                  {exitNeedsEntry && (
                    <p className="warnbox">
                      Para cerrar con el precio de salida hace falta el de entrada. Si no lo tienes,
                      escribe arriba los pips ganados o perdidos.
                    </p>
                  )}
                </div>
              </details>
            </div>

            {plan !== null && (
              <div className="preview">
                <div className="preview-h">
                  <Icon name="sparkles" />
                  Previsualizacion · la calcula la app
                </div>
                <div className="preview-grid">
                  <div>
                    <small>Riesgo</small>
                    <b>{formatPips(plan.plannedRiskPips)} pips</b>
                  </div>
                  <div>
                    <small>Objetivo</small>
                    <b>{formatPips(plan.plannedRewardPips)} pips</b>
                  </div>
                  <div>
                    <small>Riesgo-beneficio</small>
                    <b>{plan.plannedRiskReward === null ? "—" : `1:${plan.plannedRiskReward}`}</b>
                  </div>
                  {close !== null && (
                    <>
                      <div>
                        <small>Resultado</small>
                        <b className={toneOf(close.resultPips)}>
                          {formatSignedPips(close.resultPips)} pips
                        </b>
                      </div>
                      <div>
                        <small>R</small>
                        <b className={toneOf(close.rMultiple)}>{formatR(close.rMultiple)}</b>
                      </div>
                      <div>
                        <small>Captura</small>
                        <b>{close.captureRate === null ? "—" : `${Math.round(close.captureRate * 100)} %`}</b>
                      </div>
                    </>
                  )}
                </div>
                {plan.plannedRiskReward === null && (
                  <p className="hint" style={{ marginTop: 8 }}>
                    Riesgo cero: sin stop no hay R, y no es un cero.
                  </p>
                )}
                {typedPlan === null && pricePlan !== null && (
                  <p className="hint" style={{ marginTop: 8 }}>
                    Estos pips salen de los precios. Si escribes los de riesgo y objetivo, mandan
                    los escritos.
                  </p>
                )}
                {verdict.pipsDisagree && (
                  <p className="warnbox">
                    <b>Los pips escritos y los que salen de los precios no cuadran.</b> De los
                    precios salen
                    {pricePlan === null
                      ? ""
                      : ` ${formatPips(pricePlan.plannedRiskPips)} de riesgo y ${formatPips(pricePlan.plannedRewardPips)} de objetivo`}
                    {priceClose === null ? "" : ` y ${formatSignedPips(priceClose.resultPips)} de resultado`}
                    . Uno de los dos es un dedazo: se publican los escritos y la operacion queda
                    senalada hasta que lo revises.
                  </p>
                )}
                {pricePlan?.stopOnWrongSide === true && (
                  <p className="warnbox">
                    El stop esta del lado equivocado. En un{" "}
                    {draft.direction === "buy" ? "long va POR DEBAJO" : "short va POR ENCIMA"} de la
                    entrada; asi, una perdida saldria con R positivo.
                  </p>
                )}
                {pricePlan?.takeProfitOnWrongSide === true && (
                  <p className="warnbox">
                    El objetivo esta del lado equivocado. En un{" "}
                    {draft.direction === "buy" ? "long va POR ENCIMA" : "short va POR DEBAJO"} de la
                    entrada.
                  </p>
                )}
              </div>
            )}

            <div className="fblock" style={{ marginTop: 16 }}>
              <span>Como te sentiste en este trade</span>
              <div className="emo-picker">
                {list(catalogs, "emociones").map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`emo ${polarity(item)} ${draft.emotionIds.includes(item.id) ? "sel" : ""}`}
                    aria-pressed={draft.emotionIds.includes(item.id)}
                    onClick={() =>
                      set(
                        "emotionIds",
                        draft.emotionIds.includes(item.id)
                          ? draft.emotionIds.filter((id) => id !== item.id)
                          : [...draft.emotionIds, item.id],
                      )
                    }
                  >
                    {item.emoji !== null && <span aria-hidden="true">{item.emoji}</span>}
                    {item.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="fblock">
              <span>Estado mental antes de entrar</span>
              <div className="mind-picker">
                {MIND.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    className={draft.mind === item.value ? "sel" : ""}
                    title={`${item.value} de 5`}
                    aria-label={`Estado mental ${item.value} de 5`}
                    aria-pressed={draft.mind === item.value}
                    onClick={() => set("mind", item.value)}
                  >
                    {item.face}
                  </button>
                ))}
              </div>
              <div className="mind-help">{MIND[draft.mind - 1]?.help}</div>
            </div>

            <div className="fblock">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={draft.followedPlan}
                  onChange={(event) => set("followedPlan", event.target.checked)}
                />
                <span>Segui mi plan de trading</span>
              </label>

              {!draft.followedPlan && rules.length > 0 && (
                <>
                  <p className="hint" style={{ marginTop: 10 }}>
                    Marca las que SI cumpliste. Las innegociables van aparte: romper una no es un
                    porcentaje mas bajo, es otra cosa.
                  </p>
                  <div className="switch-list">
                    {rules.map((rule) => (
                      <label className="switch" key={rule.id}>
                        <span>
                          {rule.text}
                          {rule.isMandatory && <small>Innegociable</small>}
                        </span>
                        <input
                          type="checkbox"
                          checked={!draft.brokenRuleIds.includes(rule.id)}
                          onChange={(event) =>
                            set(
                              "brokenRuleIds",
                              event.target.checked
                                ? draft.brokenRuleIds.filter((id) => id !== rule.id)
                                : [...draft.brokenRuleIds, rule.id],
                            )
                          }
                        />
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            <label className="f" style={{ marginBottom: 14 }}>
              <span>Notas del trade</span>
              <textarea
                className="ctl"
                value={draft.notes}
                placeholder="Que viste, por que entraste, como gestionaste la posicion."
                onChange={(event) => set("notes", event.target.value)}
              />
              <Suggestions
                field="notes"
                onPick={(frase) => set("notes", draft.notes === "" ? frase : `${draft.notes}\n${frase}`)}
              />
            </label>

            <label className="f" style={{ marginBottom: 14 }}>
              <span>Leccion aprendida</span>
              <textarea
                className="ctl"
                style={{ minHeight: 64 }}
                value={draft.lesson}
                placeholder="Una frase que quieras volver a leer."
                onChange={(event) => set("lesson", event.target.value)}
              />
              <Suggestions
                field="lesson"
                value={draft.lesson}
                onPick={(frase) => set("lesson", frase)}
              />
            </label>

            {/* La zona de subida NO va aqui, y no por falta de servidor: las
                capturas ya funcionan. Va en el detalle porque el contrato sube
                a `POST /trades/{id}/images` y ese `id` no existe hasta que este
                formulario se guarda. Guardar los archivos en memoria y subirlos
                despues de publicar seria peor de las tres formas que importan:
                el progreso POR ARCHIVO que pide SPEC 15.6 no tendria donde
                pintarse —este modal se cierra y navega al detalle—, un fallo en
                la tercera captura llegaria cuando la pantalla que podria
                explicarlo ya no existe, y reintentarla obligaria a volver a
                elegir el archivo. Asi que se dice la verdad y se lleva alli, que
                es justo a donde el boton de abajo va a parar. */}
            <div className="fblock">
              <span>Capturas del trade</span>

              {/* Ctrl+V solo se anuncia donde existe: se pregunta por el puntero
                  fino, no por el ancho de la ventana ni por el agente de
                  usuario. En un telefono se captura y se busca el archivo, que
                  es lo que el propietario ya hace alli. */}
              {fine && (
                <p className="infobox">
                  <b>Pega una captura con Ctrl+V, aqui mismo.</b> Con la imagen en el
                  portapapeles no hay que guardarla ni buscarla: pulsa Ctrl+V en cualquier parte
                  de este formulario —no hace falta enfocar nada— y se queda esperando. Se subira
                  sola en cuanto publiques, hasta {MAX_IMAGES}.
                </p>
              )}

              {shots.length > 0 && (
                <>
                  <div className="previews">
                    {shotUrls.map((url, at) => (
                      <figure className="pv" key={url}>
                        <img src={url} alt="" />
                        <figcaption className="pv-name">Pegada</figcaption>
                        <button
                          type="button"
                          className="pv-del"
                          title="Quitar esta captura"
                          aria-label={`Quitar la captura pegada numero ${at + 1}`}
                          onClick={() =>
                            setShots((previous) => previous.filter((_, index) => index !== at))
                          }
                        >
                          <Icon name="x" />
                        </button>
                      </figure>
                    ))}
                  </div>
                  <p className="hint" aria-live="polite">
                    {shots.length === 1
                      ? "1 captura pegada, en espera. Se subira al publicar."
                      : `${shots.length} capturas pegadas, en espera. Se subiran al publicar, una a una.`}
                  </p>
                </>
              )}

              <p className="infobox">
                <b>Tambien se suben desde la operacion, en cuanto la publiques.</b> Al guardar
                aterrizas en su detalle, y ahi estan la zona de subida —hasta {MAX_IMAGES}
                capturas, una peticion por archivo y su barra de progreso— y el visor grande.
              </p>
            </div>
      </StatusBlock>
    </Modal>
  );
}

function Favs(props: {
  /** El nombre del grupo: "Simbolos favoritos". Sin el, un lector de pantalla lee cuatro botones sueltos. */
  label: string;
  items: readonly { id: string; name: string }[];
  value: string;
  onPick: (id: string) => void;
}): ReactNode {
  if (props.items.length === 0) return null;

  return (
    <div className="favs" role="group" aria-label={props.label}>
      {props.items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={props.value === item.id ? "favchip sel" : "favchip"}
          aria-pressed={props.value === item.id}
          onClick={() => props.onPick(item.id)}
        >
          <Icon name="star" />
          {item.name}
        </button>
      ))}
    </div>
  );
}

/**
 * LOS FAVORITOS PRIMERO en un desplegable, y el resto detras en su orden.
 *
 * Lo pidio el propietario al ver "Sin broker" con Weltrade ya marcado con
 * estrella: "deja Weltrade como favorito por defecto, es decir que sea el primero
 * de la lista".
 *
 * No se reordena la lista original: se concatenan dos trozos y se devuelve uno
 * nuevo. Ordenar en sitio mutaria lo que viene de la cache de `lookups`, que es
 * el mismo array que leen las otras pantallas -- y entonces el orden de un
 * desplegable dependeria de cual se abrio primero.
 */
function ordered<T extends { id: string }>(
  pool: readonly T[],
  favorites: readonly T[],
): readonly T[] {
  if (favorites.length === 0) return pool;

  const marked = new Set(favorites.map((item) => item.id));

  return [...favorites, ...pool.filter((item) => !marked.has(item.id))];
}

/** El rotulo largo de una temporalidad: el chip ensena la sigla y lo dice al pasar por encima. */
function timeframeLabel(value: string): string {
  return TIMEFRAMES.find((item) => item.value === value)?.label ?? value;
}

function list(
  catalogs: Record<string, CatalogItem[]> | undefined,
  segment: string,
): CatalogItem[] {
  return (catalogs?.[segment] ?? []).filter((item) => item.isActive);
}

/** El color del chip sale de la polaridad del catalogo, no de una tabla fija. */
function polarity(item: CatalogItem): string {
  if (item.polarity === "positive") return "good";
  if (item.polarity === "negative") return "bad";

  return "warn";
}

function blank(value: string): string | null {
  return value === "" ? null : value;
}

/** `datetime-local` quiere la hora local del dispositivo, sin zona ni segundos. */
function localNow(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;

  return new Date(now.getTime() - offset).toISOString().slice(0, 16);
}
